import {
  YES_NO,
  applicantSection,
  feeInfo,
  feeProofAttachment,
  otherAttachments,
  parcelsField,
  proxyAttachments,
  replySection,
  representationSection,
} from "modules/ebiForms/common";
import { legalTitleFields, ROAD_ORGAN } from "modules/ebiForms/definitions/sharedRoads";
import { EbiCondition, EEbiFormCategory, EEbiFormOffice, IEbiFormDefinition } from "modules/ebiForms/typings";

/**
 * E10. Wniosek o uzgodnienie lokalizacji ogrodzenia albo o zgodę na usytuowanie obiektu budowlanego przy drodze
 * powiatowej w odległości mniejszej niż ustawowa — art. 35 ust. 3, art. 39 ust. 1 i art. 43 ust. 1–2a ustawy o drogach
 * publicznych (tekst Dz.U. 2025 poz. 889 — pobrany 2026-10-22: tak; odległości od zewnętrznej krawędzi jezdni drogi
 * powiatowej: 8 m w terenie zabudowy, 20 m poza nim). Brak wzoru ogólnopolskiego; wariant „ogrodzenie” jak wzory PZD
 * Kłobuck (https://bip.pzd.klobuck.pl/download/attachment/2362/wniosek-o-uzgodnienie-lokalizacji-przebudowy-ogrodzenia.pdf
 * — pobrany: tak) i PZD Gorlice (https://pzdgorlice.pl/wp-content/uploads/2016/06/Wniosek-o-uzgodnienie-lokalizacji-ogrodzenia.pdf
 * — pobrany: tak): lokalizacja / przebudowa, działka, droga, tytuł prawny, cele wykorzystania, rowy, odległość od krawędzi
 * jezdni; wariant „obiekt” zbudowany z art. 43 ust. 2 i listy z katalogu. Walidacja 8/20 m: osobne pola odległości
 * z `max` zależnie od terenu zabudowy. Opłata za zgodę z art. 43 ust. 2 — praktyka niejednolita (do potwierdzenia
 * z zarządem dróg), dowód opłaty nieobowiązkowy.
 */
const FENCE: EbiCondition = { field: "przedmiotUzgodnienia", equals: "ogrodzenie" };
const BUILDING: EbiCondition = { field: "przedmiotUzgodnienia", equals: "obiekt" };
const BUILT_UP: EbiCondition = { all: [BUILDING, { field: "terenZabudowy", equals: "zabudowa" }] };
const OPEN_AREA: EbiCondition = { all: [BUILDING, { field: "terenZabudowy", equals: "pozaZabudowa" }] };

const E10: IEbiFormDefinition = {
  code: "E10",
  version: 1,
  title: "Wniosek o uzgodnienie lokalizacji ogrodzenia lub obiektu budowlanego przy drodze powiatowej",
  category: EEbiFormCategory.Roads,
  office: EEbiFormOffice.CountyUnit,
  description:
    "Uzgodnienie lokalizacji albo przebudowy ogrodzenia od strony drogi powiatowej lub zgoda na obiekt budowlany bliżej drogi niż odległość ustawowa.",
  keywords: ["ogrodzenie", "płot", "odległość od drogi", "obiekt budowlany", "uzgodnienie", "droga powiatowa", "zarząd dróg"],
  organ: ROAD_ORGAN.countyUnit,
  legalBasis: "art. 35 ust. 3, art. 39 ust. 1 i art. 43 ust. 1–2a ustawy z dnia 21 marca 1985 r. o drogach publicznych",
  request:
    "Wnoszę o uzgodnienie lokalizacji ogrodzenia albo o zgodę na usytuowanie obiektu budowlanego przy drodze powiatowej — zgodnie z danymi wskazanymi poniżej.",
  feeLabel: "bez opłaty",
  fee: "Uzgodnienie lokalizacji ogrodzenia — bez opłaty skarbowej; pełnomocnictwo — 17 zł. Za zgodę na mniejszą odległość obiektu (art. 43 ust. 2) część zarządców pobiera 82 zł — urząd poinformuje o opłacie.",
  deadlineLabel: "do 14 dni",
  deadline:
    "Zgoda na mniejszą odległość obiektu — 14 dni od złożenia wniosku; brak stanowiska w tym terminie oznacza zgodę (art. 43 ust. 2 w związku z art. 38 ust. 3 ustawy o drogach publicznych).",
  variantField: "przedmiotUzgodnienia",
  variants: {
    ogrodzenie: { title: "Wniosek o uzgodnienie lokalizacji ogrodzenia przy drodze powiatowej" },
    obiekt: { title: "Wniosek o zgodę na usytuowanie obiektu budowlanego przy drodze powiatowej w odległości mniejszej niż ustawowa" },
  },
  sections: [
    applicantSection(),
    representationSection(),
    {
      code: "przedmiot",
      title: "Przedmiot wniosku",
      fields: [
        {
          code: "przedmiotUzgodnienia",
          type: "radio",
          label: "Wnoszę o",
          required: true,
          options: [
            { value: "ogrodzenie", label: "uzgodnienie lokalizacji lub przebudowy ogrodzenia nieruchomości od strony drogi powiatowej" },
            {
              value: "obiekt",
              label: "zgodę na usytuowanie obiektu budowlanego w odległości od zewnętrznej krawędzi jezdni mniejszej niż ustawowa (art. 43 ust. 2)",
            },
          ],
        },
      ],
    },
    {
      code: "ogrodzenie",
      title: "Ogrodzenie",
      visibleWhen: FENCE,
      fields: [
        {
          code: "zakresOgrodzenia",
          type: "radio",
          label: "Uzgodnienie dotyczy",
          required: true,
          options: [
            { value: "lokalizacja", label: "lokalizacji nowego ogrodzenia" },
            { value: "przebudowa", label: "przebudowy istniejącego ogrodzenia" },
          ],
        },
        {
          code: "odlegloscOgrodzenia",
          type: "number",
          label: "Proponowana odległość ogrodzenia od krawędzi jezdni [m]",
          required: true,
          decimals: 2,
          min: 0,
          width: "half",
        },
        { code: "rowy", type: "radio", label: "Czy na działce od strony drogi występują rowy?", options: YES_NO },
      ],
    },
    {
      code: "obiekt",
      title: "Obiekt budowlany",
      visibleWhen: BUILDING,
      fields: [
        { code: "rodzajObiektu", type: "text", label: "Rodzaj i przeznaczenie obiektu budowlanego", required: true, maxLength: 300 },
        {
          code: "terenZabudowy",
          type: "radio",
          label: "Obiekt będzie usytuowany",
          required: true,
          options: [
            { value: "zabudowa", label: "w terenie zabudowy (odległość ustawowa od drogi powiatowej — 8 m)" },
            { value: "pozaZabudowa", label: "poza terenem zabudowy (odległość ustawowa od drogi powiatowej — 20 m)" },
          ],
        },
        {
          code: "odlegloscZabudowa",
          type: "number",
          label: "Proponowana odległość obiektu od zewnętrznej krawędzi jezdni [m]",
          required: true,
          visibleWhen: BUILT_UP,
          decimals: 2,
          min: 0,
          max: 7.99,
          width: "half",
          hint: "Mniej niż 8 m (art. 43 ust. 1).",
        },
        {
          code: "odlegloscPozaZabudowa",
          type: "number",
          label: "Proponowana odległość obiektu od zewnętrznej krawędzi jezdni [m]",
          required: true,
          visibleWhen: OPEN_AREA,
          decimals: 2,
          min: 0,
          max: 19.99,
          width: "half",
          hint: "Mniej niż 20 m (art. 43 ust. 1).",
        },
        {
          code: "ruchObiektu",
          type: "textarea",
          label: "Ruch pojazdów związany z obiektem (rodzaj pojazdów, przewidywane natężenie) i sposób włączenia go do drogi",
          required: true,
          maxLength: 2000,
        },
        {
          code: "uzasadnienie",
          type: "textarea",
          label: "Uzasadnienie usytuowania obiektu w odległości mniejszej niż ustawowa",
          required: true,
          maxLength: 3000,
          hint: "Zgoda jest możliwa tylko w szczególnie uzasadnionych przypadkach (art. 43 ust. 2).",
        },
        feeInfo(
          "infoZgoda",
          "Zgodę zarządcy drogi uzyskuje się przed pozwoleniem na budowę albo zgłoszeniem budowy i dołącza się ją do wniosku o pozwolenie na budowę albo do zgłoszenia (art. 43 ust. 2 i 2a).",
        ),
      ],
    },
    {
      code: "nieruchomosc",
      title: "Nieruchomość",
      fields: [
        { code: "drogaNumer", type: "text", label: "Numer drogi powiatowej", required: true, width: "half", maxLength: 100 },
        { code: "miejscowoscNieruchomosci", type: "text", label: "Miejscowość", required: true, width: "half", maxLength: 100 },
        { code: "ulicaNieruchomosci", type: "text", label: "Ulica i numer budynku (jeśli nadany)", maxLength: 200 },
        parcelsField(),
        ...legalTitleFields(),
        { code: "wykorzystanieDzialki", type: "text", label: "Działka jest wykorzystywana na cele", required: true, maxLength: 300 },
      ],
    },
    replySection(),
  ],
  attachments: [
    {
      code: "mapaZasadnicza",
      label: "Kopia mapy zasadniczej z naniesioną lokalizacją ogrodzenia lub obiektu i jego odległością od krawędzi jezdni",
      required: true,
    },
    {
      code: "mapaEwidencyjna",
      label: "Kopia mapy ewidencyjnej z numerami działek, w tym działki drogowej",
      required: true,
    },
    {
      code: "projektZagospodarowania",
      label: "Projekt zagospodarowania działki lub terenu albo plan sytuacyjny z usytuowaniem obiektu budowlanego",
      visibleWhen: BUILDING,
      required: true,
    },
    {
      code: "tytulPrawnyKopia",
      label: "Kopia dokumentu potwierdzającego tytuł prawny do nieruchomości",
      hint: "Nieobowiązkowo — niektórzy zarządcy dróg o nią proszą.",
    },
    {
      code: "wypisMpzp",
      label: "Wypis i wyrys z miejscowego planu zagospodarowania przestrzennego",
      hint: "Nieobowiązkowo — jeśli dla terenu obowiązuje plan miejscowy; niektórzy zarządcy dróg o niego proszą.",
    },
    feeProofAttachment("Dowód zapłaty opłaty skarbowej (82 zł)", {
      visibleWhen: BUILDING,
      required: false,
      hint: "Tylko jeśli zarząd dróg pobiera opłatę za zgodę z art. 43 ust. 2 (praktyka jest niejednolita); w sprawach budownictwa mieszkaniowego opłaty się nie pobiera (art. 2 ust. 1 pkt 2 ustawy o opłacie skarbowej).",
    }),
    ...proxyAttachments(),
    otherAttachments(),
  ],
};

export default E10;
